<template>
    <zen-slide-in :isOpen="isOpen" title="Create Webhook">
        <template #icon>
            <zen-icon-create />
        </template>
        <template #body>
            <div class="space-y-5">
                <div>
                    <label
                        for="webhook-url"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Webhook URL <span class="text-red-600">*</span>
                    </label>
                    <div class="mt-2">
                        <input
                            id="webhook-url"
                            :value="data.url"
                            @input="
                                $emit('update:data', {
                                    ...data,
                                    url: $event.target.value,
                                })
                            "
                            type="text"
                            placeholder="https://api.example.com/webhook"
                            class="block w-full rounded-md border-0 py-2 px-3 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 placeholder:text-taupe-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow"
                        />
                    </div>
                    <p class="mt-1.5 text-xs text-taupe-500">
                        The endpoint URL that will receive webhook
                        notifications.
                    </p>
                </div>

                <div>
                    <label
                        for="webhook-method"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        HTTP Method <span class="text-red-600">*</span>
                    </label>
                    <div class="mt-2">
                        <select
                            id="webhook-method"
                            :value="data.method"
                            @change="
                                $emit('update:data', {
                                    ...data,
                                    method: $event.target.value,
                                })
                            "
                            class="block w-full rounded-md border-0 py-2 px-3 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow"
                        >
                            <option>GET</option>
                            <option>POST</option>
                            <option>PUT</option>
                            <option>PATCH</option>
                        </select>
                    </div>
                    <p class="mt-1.5 text-xs text-taupe-500">
                        The HTTP method used for webhook requests.
                    </p>
                </div>

                <div>
                    <div
                        class="block text-sm font-medium leading-6 text-taupe-900 mb-3"
                    >
                        Events <span class="text-red-600">*</span>
                    </div>
                    <div class="space-y-3">
                        <div class="flex items-start">
                            <input
                                type="checkbox"
                                id="webhook-event_all"
                                :checked="data.eventAll"
                                @change="
                                    $emit('update:data', {
                                        ...data,
                                        eventAll: $event.target.checked,
                                    })
                                "
                                class="mt-0.5 h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                            />
                            <label for="webhook-event_all" class="ml-3 text-sm">
                                <span class="font-medium text-taupe-900"
                                    >All Events</span
                                >
                                <p class="text-taupe-500">
                                    Receive notifications for all current and
                                    future events
                                </p>
                            </label>
                        </div>

                        <div
                            v-if="data.eventAll"
                            class="rounded-md bg-sky-50 p-4"
                        >
                            <div class="flex">
                                <div class="flex-shrink-0">
                                    <svg
                                        class="h-5 w-5 text-sky-600"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                                            clip-rule="evenodd"
                                        />
                                    </svg>
                                </div>
                                <div class="ml-3 flex-1">
                                    <p class="text-sm text-sky-700">
                                        You'll receive all current events and
                                        automatically be subscribed to new
                                        events as they're added.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div v-if="!data.eventAll" class="space-y-2 pl-0">
                            <div
                                v-for="event in availableEvents"
                                :key="event.value"
                                class="flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    :id="'webhook-event-' + event.value"
                                    :value="event.value"
                                    :checked="
                                        data.events?.includes(event.value)
                                    "
                                    @change="toggleEvent(event.value, $event)"
                                    class="h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                                />
                                <label
                                    :for="'webhook-event-' + event.value"
                                    class="ml-3 text-sm text-taupe-700"
                                >
                                    {{ event.label }}
                                    <span class="text-taupe-500">{{
                                        event.description
                                    }}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <p class="mt-2 text-xs text-taupe-500">
                        Select at least one event type or choose "All Events" to
                        receive all notifications.
                    </p>
                </div>
            </div>
        </template>
        <template #buttons>
            <zen-button @click="$emit('close')" variant="secondary">
                Close
            </zen-button>
            <zen-button
                @click="$emit('confirm')"
                variant="primary"
                :processing="processing"
                :disabled="
                    !data.url ||
                    (!data.eventAll && (data.events?.length ?? 0) === 0)
                "
            >
                Create
            </zen-button>
        </template>
    </zen-slide-in>
</template>

<script>
export default {
    name: 'CreateWebhookSlideIn',
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        data: {
            type: Object,
            required: true,
        },
        processing: {
            type: Boolean,
            default: false,
        },
        availableEvents: {
            type: Array,
            required: true,
        },
    },
    emits: ['close', 'confirm', 'update:data'],
    methods: {
        toggleEvent(value, event) {
            const events = event.target.checked
                ? [...this.data.events, value]
                : this.data.events.filter((v) => v !== value)
            this.$emit('update:data', { ...this.data, events })
        },
    },
}
</script>
